import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ACADEMY_NAME, ACADEMY_TAGLINE, ACADEMY_MOTTO, LOGO_URL } from "@/lib/brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginAdmin } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await loginAdmin(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (e) {
      const msg = e.response?.data?.detail || "Login failed";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="Uday Career Academy" className="w-14 h-14 object-contain" />
            <div>
              <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>{ACADEMY_NAME}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">{ACADEMY_TAGLINE}</div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: "Chivo" }}>Sign in to admin</h1>
          <p className="text-slate-500 mb-10">Manage admissions, fees and reminders from one place.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="label-caps">Email</Label>
              <Input id="email" name="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12" required autoComplete="username" placeholder="you@udaycareeracademy.com" />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input id="password" name="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12" required autoComplete="current-password" placeholder="Enter your password" />
            </div>
            {err && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="login-error">
                <AlertCircle size={16} className="mt-0.5" />{err}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500 mb-3">Are you a student?</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" data-testid="goto-student-login" onClick={() => navigate("/student-login")} className="h-11">
                Login (OTP)
              </Button>
              <Button variant="outline" data-testid="goto-student-register" onClick={() => navigate("/register")} className="h-11">
                Register
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right hero with logo */}
      <div className="hidden lg:flex relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 items-center justify-center p-12">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(132,204,22,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(245,158,11,0.3) 0%, transparent 50%)" }} />
        <div className="relative text-center text-white max-w-lg">
          <img src={LOGO_URL} alt="logo" className="w-64 h-64 object-contain mx-auto mb-8 drop-shadow-2xl" />
          <div className="label-caps text-lime-300 mb-3">Mission Ready</div>
          <h2 className="text-4xl font-bold leading-tight mb-3" style={{ fontFamily: "Chivo" }}>
            {ACADEMY_NAME}
          </h2>
          <p className="text-slate-200 text-lg" style={{ fontFamily: "Chivo" }}>|| {ACADEMY_MOTTO} ||</p>
          <p className="mt-6 text-slate-300 text-sm">Police & Army Bharti coaching · Nashik</p>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
